/**
 * Helpers for simulation question/task navigation (unanswered detection, labels).
 *
 * Used by Reading, Listening (review), and Writing modules.
 *
 * @module simulationQuestionNavigation
 */

import type { Question, TextChunk } from '@/types/exam-simulation';

/** Item shown in the question/task navigator dialog */
export interface SimulationNavItem {
  readonly id: string;
  readonly label: string;
  readonly detail?: string;
  readonly isAnswered: boolean;
  /** Reading/listening MCQ: learner flagged for review */
  readonly isFlagged?: boolean;
}

/**
 * Whether a multiple-choice-single style answer counts as answered (non-empty after trim).
 */
export function isMcqAnswered(
  selectedAnswers: Record<number, string>,
  questionId: number,
): boolean {
  const v = selectedAnswers[questionId];
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * Index of the reading text chunk that contains the given question id, or -1 if none.
 */
export function findChunkIndexForQuestionId(
  chunks: ReadonlyArray<Pick<TextChunk, 'questionIds'>>,
  questionId: number,
): number {
  return chunks.findIndex((c) => c.questionIds.includes(questionId));
}

/**
 * Index of the listening section whose `questions` array contains the given id, or -1 if none.
 */
export function findListeningSectionIndexForQuestionId(
  sections: ReadonlyArray<{ readonly questions: ReadonlyArray<{ readonly id: number }> }>,
  questionId: number,
): number {
  return sections.findIndex((s) => s.questions.some((q) => q.id === questionId));
}

/**
 * Build navigator rows for MCQ questions (reading / listening).
 */
export function buildMcqNavigatorItems(
  questions: Question[],
  selectedAnswers: Record<number, string>,
  flaggedQuestionIds: readonly number[] = [],
): SimulationNavItem[] {
  const flagged = new Set(flaggedQuestionIds);
  return questions.map((q) => ({
    id: String(q.id),
    label: `Q${q.id}`,
    detail: truncateStem(q.stem),
    isAnswered: isMcqAnswered(selectedAnswers, q.id),
    isFlagged: flagged.has(q.id),
  }));
}

/** Navigator rows that still need an answer (same definition as task overview). */
export function filterUnansweredItems(items: readonly SimulationNavItem[]): SimulationNavItem[] {
  return items.filter((item) => !item.isAnswered);
}

function truncateStem(stem: string, max = 72): string {
  const t = stem.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

export interface FillBlankFormLike {
  readonly blanks: ReadonlyArray<{ readonly id: string }>;
}

/**
 * Whether a specific fill-in-blank answer counts as answered (non-empty after trim).
 */
export function isFillBlankAnswered(
  fillBlankAnswers: Record<string, string>,
  blankId: string,
): boolean {
  const v = fillBlankAnswers[blankId];
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * Number of answered fill-in-blank inputs in a form.
 */
export function countAnsweredFillBlankItems(
  form: FillBlankFormLike | null | undefined,
  fillBlankAnswers: Record<string, string>,
): number {
  if (!form?.blanks?.length) return 0;
  return form.blanks.reduce((count, blank) => {
    return count + (isFillBlankAnswered(fillBlankAnswers, blank.id) ? 1 : 0);
  }, 0);
}

/**
 * Fill-in-the-blank navigator items (chunk 0 when fill-in-blank exists).
 */
export function buildFillBlankNavigatorItems(
  form: FillBlankFormLike | null | undefined,
  fillBlankAnswers: Record<string, string>,
  labelForIndex: (index: number) => string,
): SimulationNavItem[] {
  if (!form?.blanks?.length) return [];
  return form.blanks.map((blank, i) => {
    return {
      id: `blank:${blank.id}`,
      label: labelForIndex(i + 1),
      isAnswered: isFillBlankAnswered(fillBlankAnswers, blank.id),
    };
  });
}

/**
 * Essay task: answered for progress/navigator when the learner has entered at least one
 * non-whitespace character. Finish validation and word minimum still apply separately.
 */
export function isEssayAnswered(essayContent: string): boolean {
  return essayContent.trim().length > 0;
}

export function getEssayWordCount(essayContent: string): number {
  const trimmed = essayContent.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

/**
 * Minimal essay task shape consumed by the navigator helpers. Matches
 * `EssayTaskData` from `writingDataTransform` but kept local so this util
 * stays self-contained and easy to test.
 */
export interface EssayTaskLike {
  readonly questionId: string;
  readonly taskType?: string;
}

export interface BuildWritingNavParams {
  readonly hasFillBlank: boolean;
  readonly hasEssay: boolean;
  readonly fillBlankForms: ReadonlyArray<FillBlankFormLike> | null | undefined;
  readonly fillBlankAnswers: Record<string, string>;
  /**
   * Essay tasks in display order. Each task becomes its own navigator item
   * with `id` = `essay:<questionId>`.
   */
  readonly essayTasks: ReadonlyArray<EssayTaskLike> | null | undefined;
  /**
   * Essay content keyed by `essayTasks[i].questionId`. Missing entries count
   * as unanswered.
   */
  readonly essayContentByTask: Readonly<Record<string, string>>;
  readonly blankLabel: (index: number) => string;
  /**
   * Label for an essay task. Receives the 1-based task index and the task
   * itself so callers can decide whether to use the per-task `taskType` or a
   * generic "Essay N" label.
   */
  readonly essayLabel: (index: number, task: EssayTaskLike) => string;
}

/**
 * All writing tasks in order: fill-in-blank blanks first (chunk 0), then one
 * navigator entry per essay task. The count of essay items follows the data
 * — A1 typically ships one, B1 Goethe ships three.
 */
export function buildAllWritingNavigatorItems(params: BuildWritingNavParams): SimulationNavItem[] {
  const items: SimulationNavItem[] = [];
  const form = params.hasFillBlank ? params.fillBlankForms?.[0] : undefined;
  items.push(
    ...buildFillBlankNavigatorItems(form, params.fillBlankAnswers, params.blankLabel),
  );
  if (params.hasEssay && params.essayTasks?.length) {
    params.essayTasks.forEach((task, index) => {
      const content = params.essayContentByTask[task.questionId] ?? '';
      items.push({
        id: `essay:${task.questionId}`,
        label: params.essayLabel(index + 1, task),
        isAnswered: isEssayAnswered(content),
      });
    });
  }
  return items;
}

export interface WritingProgressCounts {
  readonly answered: number;
  readonly total: number;
}

export function getNavigatorProgressCounts(
  items: readonly SimulationNavItem[],
): WritingProgressCounts {
  return {
    answered: items.filter((item) => item.isAnswered).length,
    total: items.length,
  };
}

/**
 * Module-wide writing progress counts used by progress pills/bars.
 * Includes all fill-in-blank items and the essay item (when present).
 */
export function getWritingProgressCounts(params: BuildWritingNavParams): WritingProgressCounts {
  return getNavigatorProgressCounts(buildAllWritingNavigatorItems(params));
}

export interface WritingFinishValidationParams extends BuildWritingNavParams {
  readonly minWords: number;
}

export interface WritingFinishValidation {
  readonly unansweredItems: SimulationNavItem[];
  readonly essayWordCount: number;
  readonly shouldConfirmUnanswered: boolean;
  readonly shouldConfirmUnderWordLimit: boolean;
}

export interface WritingFinishValidationFromItemsParams {
  readonly items: readonly SimulationNavItem[];
  readonly hasEssay: boolean;
  /**
   * Essay content of the essay task the learner is currently viewing. The
   * under-word warning is tied to the current essay (matches existing UX
   * where the user is prompted before submitting the essay they're on).
   */
  readonly essayContent: string;
  readonly minWords: number;
}

export function getWritingFinishValidationFromItems(
  params: WritingFinishValidationFromItemsParams,
): WritingFinishValidation {
  const unansweredItems = filterUnansweredItems(params.items);
  const essayWordCount = getEssayWordCount(params.essayContent);
  const shouldConfirmUnanswered = unansweredItems.length > 0;
  const shouldConfirmUnderWordLimit =
    !shouldConfirmUnanswered && params.hasEssay && essayWordCount < params.minWords;
  return {
    unansweredItems,
    essayWordCount,
    shouldConfirmUnanswered,
    shouldConfirmUnderWordLimit,
  };
}

/**
 * Shared writing finish validation order:
 * 1) unanswered tasks across the full module, 2) essay min-word warning.
 */
export function getWritingFinishValidation(
  params: WritingFinishValidationParams & { readonly currentEssayContent: string },
): WritingFinishValidation {
  return getWritingFinishValidationFromItems({
    items: buildAllWritingNavigatorItems(params),
    hasEssay: params.hasEssay,
    essayContent: params.currentEssayContent,
    minWords: params.minWords,
  });
}

/**
 * Chunk index for a writing navigator item id.
 *
 * Layout: chunk 0 = fill-in-blank (if present), then one chunk per essay
 * task in `essayTaskIds` order.
 */
export function getWritingJumpChunkIndex(
  itemId: string,
  hasFillBlank: boolean,
  essayTaskIds: readonly string[],
): number {
  const essayBase = hasFillBlank ? 1 : 0;
  if (itemId.startsWith('essay:')) {
    const questionId = itemId.slice('essay:'.length);
    const index = essayTaskIds.indexOf(questionId);
    return index === -1 ? essayBase : essayBase + index;
  }
  if (itemId.startsWith('blank:')) {
    return 0;
  }
  return 0;
}

export interface GetWritingNotesTaskKeyParams {
  readonly currentChunk: number;
  readonly hasFillBlank: boolean;
  readonly hasEssay: boolean;
  readonly fillBlankQuestionId?: string | null;
  readonly essayQuestionId?: string | null;
}

/**
 * Stable storage key for writing scratchpad notes. Notes are per task, not per
 * mounted prompt panel, so switching between fill-in-blank and essay tasks keeps
 * each scratchpad isolated.
 */
export function getWritingNotesTaskKey({
  currentChunk,
  hasFillBlank,
  hasEssay,
  fillBlankQuestionId,
  essayQuestionId,
}: GetWritingNotesTaskKeyParams): string {
  if (currentChunk === 0 && hasFillBlank) {
    return fillBlankQuestionId ? `fill-in-blank:${fillBlankQuestionId}` : `chunk:${currentChunk}`;
  }
  if (
    hasEssay &&
    ((currentChunk === 0 && !hasFillBlank) || (currentChunk === 1 && hasFillBlank))
  ) {
    return essayQuestionId ? `essay:${essayQuestionId}` : `chunk:${currentChunk}`;
  }
  return `chunk:${currentChunk}`;
}
